import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, Lightbulb, Users, Heart, Award, Beaker } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const AboutPage = () => {
  const navigate = useNavigate();

  const coreValues = [
    {
      icon: <Lightbulb className="w-6 h-6" />,
      title: "Innovation",
      description: "Pushing the boundaries of AI-powered laboratory analysis to deliver cutting-edge solutions.",
      color: "from-amber-500 to-orange-500"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Accuracy",
      description: "Committed to providing precise and reliable analytical results that scientists can trust.",
      color: "from-cyan-500 to-blue-500"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Accessibility",
      description: "Making advanced spectral analysis accessible to laboratories of all sizes worldwide.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "User-Centric",
      description: "Designing every feature with the scientist's workflow and needs in mind.",
      color: "from-rose-500 to-red-500"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEO 
        title="About Us"
        description="Learn about TabedLab's mission to revolutionize laboratory analysis with AI. Meet our founder and discover our core values of innovation, accuracy, and accessibility."
        keywords="TabedLab, about us, AI laboratory, spectroscopy company, chromatography analysis, scientific analysis platform"
        url="/about"
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/tabedlab-logo.png" 
              alt="TabedLab Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              TabedLab
            </span>
          </div>
          
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
            data-testid="back-to-home-button"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-40 right-1/4 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">About </span>
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">TabedLab</span>
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Empowering scientists and laboratories with AI-driven spectral analysis technology.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800/60 to-slate-800/30 border border-slate-700 rounded-3xl p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl"></div>
            
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
                  <Target className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-cyan-300">Our Mission</span>
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Transforming Laboratory Analysis
                </h2>
                <p className="text-slate-400 text-lg leading-relaxed mb-4">
                  At TabedLab, we believe that every scientist deserves access to powerful analytical tools. Our mission is to democratize spectral analysis by combining advanced AI technology with user-friendly interfaces.
                </p>
                <p className="text-slate-400 text-lg leading-relaxed">
                  We're committed to reducing the time from data collection to actionable insights, enabling researchers to focus on what matters most: scientific discovery.
                </p>
              </div>
              <div className="flex items-center justify-center">
                <div className="relative">
                  <div className="w-64 h-64 rounded-3xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 flex items-center justify-center">
                    <Beaker className="w-24 h-24 text-cyan-400" />
                  </div>
                  <div className="absolute -top-4 -right-4 w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500/30 to-pink-500/30 border border-purple-500/30 flex items-center justify-center">
                    <Lightbulb className="w-10 h-10 text-purple-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why TabedLab Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              <span className="text-white">Why </span>
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">TabedLab?</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              We combine deep scientific expertise with cutting-edge AI to deliver unmatched spectral analysis capabilities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">For Researchers</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 mt-2 flex-shrink-0"></span>
                  <span>Instant interpretation of complex spectral data</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 mt-2 flex-shrink-0"></span>
                  <span>Automated peak detection and functional group identification</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 mt-2 flex-shrink-0"></span>
                  <span>Publication-ready reports and visualizations</span>
                </li>
              </ul>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">For Laboratories</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="w-2 h-2 rounded-full bg-blue-400 mt-2 flex-shrink-0"></span>
                  <span>Streamlined workflow integration</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="w-2 h-2 rounded-full bg-blue-400 mt-2 flex-shrink-0"></span>
                  <span>Consistent and reproducible analysis standards</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400">
                  <span className="w-2 h-2 rounded-full bg-blue-400 mt-2 flex-shrink-0"></span>
                  <span>Cost-effective solution for high-throughput analysis</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              <span className="text-white">Our </span>
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Core Values</span>
            </h2>
            <p className="text-slate-400 text-lg">
              The principles that guide everything we do at TabedLab.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, index) => (
              <div 
                key={index}
                className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 text-center hover:border-slate-600 transition-colors"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center mx-auto mb-4 text-white`}>
                  {value.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{value.title}</h3>
                <p className="text-slate-400 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              <span className="text-white">Meet the </span>
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Founder</span>
            </h2>
          </div>

          <div className="bg-gradient-to-br from-slate-800/60 to-slate-800/30 border border-slate-700 rounded-3xl p-10 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl"></div>
            
            <div className="relative text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 mb-6 shadow-lg shadow-cyan-500/20">
                <Beaker className="w-10 h-10 text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-white mb-2">Obed Ducles</h3>
              <p className="text-cyan-400 font-medium mb-6">Chemical Engineer | Data Analyst</p>
              <p className="text-slate-400 leading-relaxed mb-4 max-w-2xl mx-auto">
                With a passion for bridging the gap between laboratory science and modern technology, Obed founded TabedLab to revolutionize how researchers interact with their spectral data.
              </p>
              <p className="text-slate-400 leading-relaxed max-w-2xl mx-auto">
                Drawing from years of experience in chemical engineering and data analysis, Obed envisioned a platform where AI could augment human expertise, making complex spectral interpretation accessible to scientists everywhere.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            <span className="text-white">Ready to </span>
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Get Started?</span>
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of scientists who trust TabedLab for their spectral analysis needs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/register')}
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg px-10 py-6 rounded-xl"
              data-testid="about-cta-register"
            >
              Create Free Account
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/demo')}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white text-lg px-10 py-6 rounded-xl"
              data-testid="about-cta-demo"
            >
              Try Demo First
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-950 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          <p>&copy; {new Date().getFullYear()} TabedLab. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
